import { BarChart3 } from 'lucide-react'
import { useState } from 'react'
import { useOutletContext } from 'react-router'

import { cambio, DISPOSITIVOS, nombreDePais, type ResumenDeVisitas, useVisitas } from '../api/visitas'
import { Aviso, clases, Esqueleto, Tarjeta, Titulo } from '../componentes/ui'
import type { Contexto } from './Modulo'

/**
 * «Visitas» de una web (CLAUDE.md §34): cuántas visitas y cuántas personas, día a día; qué páginas se
 * miran, de dónde llega la gente, desde qué dispositivo y de qué país, por qué campaña, y qué
 * formularios convierten. Lo cuenta el enrutador al servir cada página, **sin cookies** y sin guardar
 * quién: por eso no hace falta ningún aviso de cookies para esto.
 */
const PERIODOS = [
  { dias: 7, texto: '7 días' },
  { dias: 30, texto: '30 días' },
  { dias: 90, texto: '90 días' },
  { dias: 365, texto: 'Un año' },
] as const

const numero = (n: number) => n.toLocaleString('es')
const diaCorto = (dia: string) => new Date(`${dia}T12:00:00Z`).toLocaleDateString('es', { timeZone: 'UTC', day: 'numeric', month: 'short' })

const Cifra = ({ etiqueta, valor, antes }: { etiqueta: string; valor: number; antes: number }) => {
  const c = cambio(valor, antes)
  return (
    <Tarjeta>
      <p className="text-[13px] text-tenue">{etiqueta}</p>
      <p className="mt-1 font-display text-2xl font-semibold tabular-nums">{numero(valor)}</p>
      <p className={clases('text-xs', !c ? 'text-tenue' : c.startsWith('−') ? 'text-error' : 'text-ok')}>{c ? `${c} respecto a los días anteriores` : 'Sin datos de antes para comparar'}</p>
    </Tarjeta>
  )
}

/** Una lista de «valor — visitas» con su barra, como en cualquier analítica. */
const Lista = ({ titulo, filas, vacio }: { titulo: string; filas: Array<{ clave: string; texto: React.ReactNode; visitas: number }>; vacio: string }) => {
  const maximo = Math.max(1, ...filas.map((f) => f.visitas))
  return (
    <Tarjeta>
      <h2 className="font-display text-base font-semibold">{titulo}</h2>
      {filas.length === 0 ? (
        <p className="mt-2 text-[13px] text-tenue">{vacio}</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {filas.map((f) => (
            <li className="relative flex items-center justify-between gap-3 overflow-hidden rounded-md px-2 py-1 text-[13px]" key={f.clave}>
              <span aria-hidden className="absolute inset-y-0 left-0 rounded-md bg-violeta-suave" style={{ width: `${(f.visitas / maximo) * 100}%` }} />
              <span className="relative min-w-0 truncate">{f.texto}</span>
              <span className="relative shrink-0 tabular-nums text-tinta-2">{numero(f.visitas)}</span>
            </li>
          ))}
        </ul>
      )}
    </Tarjeta>
  )
}

const Serie = ({ serie }: { serie: ResumenDeVisitas['serie'] }) => {
  const maximo = Math.max(1, ...serie.map((d) => d.visitas))
  const etiquetas = serie.length <= 31 ? 7 : 6
  const cada = Math.max(1, Math.ceil(serie.length / etiquetas))
  return (
    <Tarjeta>
      <h2 className="font-display text-base font-semibold">Visitas por día</h2>
      <div aria-label="Visitas por día" className="mt-4 flex h-36 items-end gap-px" role="img">
        {serie.map((d) => (
          <div className="group relative flex h-full flex-1 items-end" key={d.dia} title={`${diaCorto(d.dia)}: ${numero(d.visitas)} visitas, ${numero(d.personas)} personas`}>
            <div className="w-full rounded-t-sm bg-violeta/70 group-hover:bg-violeta" style={{ height: `${d.visitas > 0 ? Math.max(3, (d.visitas / maximo) * 100) : 0}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-1 flex text-[11px] text-tenue">
        {serie.map((d, i) => (
          <span className="flex-1 text-center" key={d.dia}>
            {i % cada === 0 ? diaCorto(d.dia) : ''}
          </span>
        ))}
      </div>
    </Tarjeta>
  )
}

export const Visitas = () => {
  const { sitio } = useOutletContext<Contexto>()
  const [dias, setDias] = useState(30)
  const { data, isPending, error } = useVisitas(sitio?.id ?? 0, dias, Boolean(sitio))
  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  const titulo = (
    <Titulo descripcion="Quién visita tu web y qué mira. Lo contamos al servir cada página, sin cookies y sin guardar quién es cada persona." rotulo={sitio.nombre}>
      Visitas
    </Titulo>
  )
  const selector = (
    <div aria-label="Periodo" className="mb-4 inline-flex rounded-boton border border-linea p-0.5" role="group">
      {PERIODOS.map((p) => (
        <button
          aria-pressed={dias === p.dias}
          className={clases('rounded-[6px] px-3 py-1 text-[13px]', dias === p.dias ? 'bg-violeta text-white' : 'text-tinta-2 hover:bg-papel-2')}
          key={p.dias}
          onClick={() => setDias(p.dias)}
          type="button"
        >
          {p.texto}
        </button>
      ))}
    </div>
  )
  if (isPending) return (<>{titulo}{selector}<Esqueleto /></>)
  if (error) return (<>{titulo}<Aviso tono="error">No hemos podido leer las visitas. Vuelve a intentarlo en un momento.</Aviso></>)
  if (data.totales.visitas === 0 && data.anteriores.visitas === 0) {
    return (
      <>
        {titulo}
        {selector}
        <Tarjeta>
          <div className="flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-violeta-suave text-violeta">
              <BarChart3 className="size-5" />
            </span>
            <p className="text-[13px] text-tinta-2">Todavía no hay visitas que contar en estos días. En cuanto tu web esté publicada y alguien la abra, las verás aquí (se apuntan cada minuto).</p>
          </div>
        </Tarjeta>
      </>
    )
  }
  return (
    <>
      {titulo}
      {selector}
      <div className="flex flex-col gap-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Cifra antes={data.anteriores.visitas} etiqueta="Visitas" valor={data.totales.visitas} />
          <Cifra antes={data.anteriores.personas} etiqueta="Personas distintas (cada día)" valor={data.totales.personas} />
        </div>
        <Serie serie={data.serie} />
        <div className="grid gap-6 lg:grid-cols-2">
          <Lista
            filas={data.paginas.map((p) => ({ clave: p.ruta, texto: p.titulo ? (<><span className="font-medium">{p.titulo}</span> <span className="text-tenue">{p.ruta}</span></>) : p.ruta, visitas: p.visitas }))}
            titulo="Páginas más vistas"
            vacio="Ninguna todavía."
          />
          <Lista
            filas={data.referentes.map((r) => ({ clave: r.valor, texto: r.valor, visitas: r.visitas }))}
            titulo="De dónde llegan"
            vacio="Todas las visitas llegaron directas (escribiendo la dirección, desde un favorito o una aplicación)."
          />
          <Lista filas={data.paises.map((p) => ({ clave: p.valor, texto: nombreDePais(p.valor), visitas: p.visitas }))} titulo="Países" vacio="Sin datos de país." />
          <Lista filas={data.dispositivos.map((d) => ({ clave: d.valor, texto: DISPOSITIVOS[d.valor] ?? d.valor, visitas: d.visitas }))} titulo="Dispositivos" vacio="Sin datos." />
          {data.campanas.length > 0 && (
            <Lista filas={data.campanas.map((c) => ({ clave: c.valor, texto: c.valor, visitas: c.visitas }))} titulo="Campañas (utm_source)" vacio="" />
          )}
        </div>
        <Tarjeta>
          <h2 className="font-display text-base font-semibold">Qué formularios convierten</h2>
          <p className="mt-1 text-[13px] text-tenue">Respuestas completas (sin spam) entre las visitas de las páginas donde sale cada formulario.</p>
          {data.formularios.length === 0 ? (
            <p className="mt-2 text-[13px] text-tenue">Ningún formulario ha tenido visitas ni respuestas en estos días.</p>
          ) : (
            <table className="mt-3 w-full text-[13px]">
              <thead className="text-left text-tenue">
                <tr>
                  <th className="py-1 font-normal">Formulario</th>
                  <th className="py-1 text-right font-normal">Visitas</th>
                  <th className="py-1 text-right font-normal">Respuestas</th>
                  <th className="py-1 text-right font-normal">Conversión</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-linea">
                {data.formularios.map((f) => (
                  <tr key={f.id}>
                    <td className="py-1.5">{f.nombre}</td>
                    <td className="py-1.5 text-right tabular-nums">{numero(f.visitas)}</td>
                    <td className="py-1.5 text-right tabular-nums">{numero(f.respuestas)}</td>
                    <td className="py-1.5 text-right tabular-nums">{f.conversion === null ? '—' : `${(f.conversion * 100).toLocaleString('es', { maximumFractionDigits: 1 })} %`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Tarjeta>
      </div>
    </>
  )
}
